import React, { useCallback, useEffect, useState } from 'react';
import { Loader, Save, Upload, Video } from 'lucide-react';
import { toast } from 'react-toastify';
import { SettingsService } from '../../services/settingsService';
import { CoachesService } from '../../services/coachesService';
import { GeneralSettings } from '../../types/settings';
import { Coach } from '../../types/coach';

const GeneralSection = () => {
  const [settings, setSettings] = useState<GeneralSettings>({
    intro_video: '',
    default_coach: '',
  });
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [selectedCoachId, setSelectedCoachId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string>('');

  const loadSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const settingsData = await SettingsService.getGeneralSettings();
      setSettings(settingsData);
      setSelectedCoachId(settingsData.default_coach || '');
      setPreviewVideoUrl(settingsData.intro_video || '');
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadCoaches = useCallback(async () => {
    try {
      const coachesData = await CoachesService.getAllCoaches();
      setCoaches(coachesData);
    } catch (error) {
      console.error('Error loading coaches:', error);
      toast.error('Failed to load coaches', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadCoaches();
  }, [loadSettings, loadCoaches]);

  const handleVideoFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        toast.error('Please select a video file', {
          position: 'top-right',
          autoClose: 3000,
        });
        return;
      }
      if (file.size > 100 * 1024 * 1024) {
        toast.error('Video file size must be less than 100MB', {
          position: 'top-right',
          autoClose: 3000,
        });
        return;
      }
      setSelectedVideoFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewVideoUrl(objectUrl);
    }
  };

  const handleUploadVideo = async () => {
    if (!selectedVideoFile) {
      toast.error('Please select a video file', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const downloadURL = await SettingsService.uploadIntroVideo(
        selectedVideoFile,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      await SettingsService.updateIntroVideo(downloadURL);

      setSettings((prev) => ({ ...prev, intro_video: downloadURL }));
      setPreviewVideoUrl(downloadURL);
      setSelectedVideoFile(null);

      toast.success('Intro video uploaded successfully!', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Failed to upload video. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSaveDefaultCoach = async () => {
    if (!selectedCoachId) {
      toast.error('Please select a default coach', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setIsSaving(true);
    try {
      await SettingsService.updateDefaultCoach(selectedCoachId);
      setSettings((prev) => ({ ...prev, default_coach: selectedCoachId }));

      toast.success('Default coach saved successfully!', {
        position: 'top-right',
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error saving default coach:', error);
      toast.error('Failed to save default coach. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="size-6 animate-spin text-custom-500" />
        <span className="ml-2 text-slate-500">Loading settings...</span>
      </div>
    );
  }

  const selectedCoach = coaches.find((coach) => coach.id === selectedCoachId);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
      <div className="p-4 rounded-md border border-slate-200 dark:border-zink-500">
        <h6 className="text-15 font-semibold">Intro Video</h6>
        <p className="text-sm text-slate-500 dark:text-zink-400 mt-1 mb-4">
          Played to students when they first open the app.
        </p>

        {previewVideoUrl ? (
          <div className="mb-4 rounded-lg overflow-hidden bg-slate-100 dark:bg-zink-700">
            <video
              src={previewVideoUrl}
              controls
              className="w-full h-auto max-h-[260px] object-contain"
              onError={(e) => {
                console.error('Error loading video:', e);
              }}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        ) : (
          <div className="mb-4 p-8 border-2 border-dashed border-slate-300 dark:border-zink-500 rounded-lg text-center">
            <Video className="size-12 text-slate-400 dark:text-zink-500 mx-auto mb-2" />
            <p className="text-sm text-slate-500 dark:text-zink-400">
              No intro video uploaded yet
            </p>
          </div>
        )}

        <label className="block text-sm font-medium mb-2">Upload New Video</label>
        <div className="flex gap-2">
          <label className="flex-1 cursor-pointer">
            <div className="flex items-center justify-center px-4 py-2.5 border border-slate-300 dark:border-zink-500 rounded-md hover:bg-slate-50 dark:hover:bg-zink-600 transition-colors">
              <Upload className="size-4 mr-2" />
              <span className="text-sm truncate">
                {selectedVideoFile ? selectedVideoFile.name : 'Choose Video File'}
              </span>
            </div>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoFileSelect}
              className="hidden"
              disabled={isUploading}
            />
          </label>
          {selectedVideoFile && (
            <button
              onClick={handleUploadVideo}
              disabled={isUploading}
              className="px-4 py-2.5 bg-custom-500 text-white rounded-md hover:bg-custom-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUploading ? (
                <>
                  <Loader className="inline-block size-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Upload'
              )}
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 dark:text-zink-400 mt-2">
          Supported formats: MP4, WebM, MOV. Max size: 100MB
        </p>

        {isUploading && (
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-600 dark:text-zink-300">Upload Progress</span>
              <span className="text-slate-600 dark:text-zink-300">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-slate-200 dark:bg-zink-600 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-custom-500 h-2.5 transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      <div className="p-4 rounded-md border border-slate-200 dark:border-zink-500 h-fit">
        <h6 className="text-15 font-semibold">Default Coach</h6>
        <p className="text-sm text-slate-500 dark:text-zink-400 mt-1 mb-4">
          Automatically assigned to new students on registration.
        </p>

        <div className="mb-4">
          <select
            value={selectedCoachId}
            onChange={(e) => setSelectedCoachId(e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:text-zink-100"
            disabled={isSaving || coaches.length === 0}
          >
            <option value="">Select a coach...</option>
            {coaches.map((coach) => (
              <option key={coach.id} value={coach.id}>
                {coach.fullName} ({coach.email})
              </option>
            ))}
          </select>
          {coaches.length === 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              No coaches available. Please add coaches first.
            </p>
          )}
        </div>

        {selectedCoach && (
          <div className="mb-4 p-4 border border-slate-200 dark:border-zink-500 rounded-lg">
            <label className="block text-sm font-medium mb-3">Selected Coach</label>
            <div className="flex items-center gap-3">
              {selectedCoach.photoUrl ? (
                <img
                  src={selectedCoach.photoUrl}
                  alt={selectedCoach.fullName}
                  className="size-12 rounded-full object-cover"
                />
              ) : (
                <div className="size-12 rounded-full bg-slate-200 dark:bg-zink-600 flex items-center justify-center">
                  <span className="text-lg font-semibold text-slate-600 dark:text-zink-300">
                    {selectedCoach.fullName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <p className="font-medium text-slate-800 dark:text-zink-100">
                  {selectedCoach.fullName}
                </p>
                <p className="text-sm text-slate-500 dark:text-zink-400">
                  {selectedCoach.email}
                </p>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={handleSaveDefaultCoach}
          disabled={isSaving || !selectedCoachId || selectedCoachId === settings.default_coach}
          className="w-full px-4 py-2.5 bg-custom-500 text-white rounded-md hover:bg-custom-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {isSaving ? (
            <>
              <Loader className="size-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="size-4 mr-2" />
              Save Default Coach
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default GeneralSection;
